import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  Content,
  ContentBody,
  ContentButton,
  ContentHeader,
  ContentIcon,
  ContentTitle
} from 'components/Content'
import KanBoIcon from 'components/KanBoIcon'
import {
  ToggleModal
} from 'components/Modal'
import AddOfficeDocumentForm from '../AddOfficeDocumentForm'
import styled from 'styled-components'
import AddElementButton from '../AddElementButton'
import ElementContentIcon from '../ContentIcon'
import requestAddDocumentFromTemplate from '../sagas/requestAddDocumentFromTemplate'
import { useDispatch, useSelector } from 'react-redux'
import { useBoardContext } from 'dataProviders/Board'
import { ModalWithBoardContext } from 'components/Modal'
import {
  LeftSidebarView,
  ViewBody,
  BoardModalView,
} from 'components/Sidebar'
import { DocumentTemplatesList, DocumentTemplateItem } from 'routes/Board/DocumentTemplates'
import {
  FormGroup,
  ControlLabel,
} from 'react-bootstrap'
import withLeftSidebar, { LeftSidebarProps } from 'components/Sidebar/withLeftSidebar'

const AddOfficeDocumentContent = styled(Content)`
  background-color: #eceff1;
`

const DOCUMENT = {
  FROM_TEMPLATE: 'from_template',
  WORD: 'word',
  EXCEL: 'excel',
  POWERPOINT: 'powerpoint',
} as const

type AddDocumentType = typeof DOCUMENT[keyof typeof DOCUMENT]

const DOCUMENTS = [{
  id: DOCUMENT.FROM_TEMPLATE,
  iconName: 'kanbo-24-add-new-document-from-template',
  formattedMessageId: 'DOCUMENT_ADD_FROM_TEMPLATE',
}, {
  id: DOCUMENT.WORD,
  iconName: 'kanbo-24-create-new-word-document',
  formattedMessageId: 'DOCUMENT_WORD_ADD_NEW',
}, {
  id: DOCUMENT.EXCEL,
  iconName: 'kanbo-24-create-new-excel-document',
  formattedMessageId: 'DOCUMENT_EXCEL_ADD_NEW',
}, {
  id: DOCUMENT.POWERPOINT,
  iconName: 'kanbo-24-create-new-powerpoint-document',
  formattedMessageId: 'DOCUMENT_POWERPOINT_ADD_NEW',
}]

type Props = LeftSidebarProps & {
  documentFolderId: number;
  url: string;
  refreshDocuments: () => void;
}

const AddDocumentButton: React.FC<Props> = ({
  leftSidebar,
  documentFolderId,
  url,
  refreshDocuments,
}) => {
  const dispatch = useDispatch()

  const boardContext = useBoardContext()

  const buttonRef = useRef<HTMLDivElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)

  const urlRef = useRef(url)
  useEffect(() => {
    urlRef.current = url
  }, [url])

  const [addingDocument, setAddingDocument] = useState<boolean | AddDocumentType>(false)

  const documentSourceId = useSelector<any, number>(state => state['view-card'].elements.elementsById[documentFolderId]?.documentSourceId)

  const showToggleModal = useMemo(() => {
    return addingDocument && addingDocument !== DOCUMENT.FROM_TEMPLATE
  }, [addingDocument])

  useLayoutEffect(() => {
    if (showToggleModal && modalRef.current && buttonRef.current) {
      modalRef.current.style.width = `${buttonRef.current.offsetWidth}px`
    }
  }, [showToggleModal])

  const handleAddItemClick = useCallback(() => {
    setAddingDocument(true)
  }, [])

  const handleAddItemClose = useCallback(() => {
    setAddingDocument(false)
  }, [])

  const handleAddDocumentFromTemplateSubmit = useCallback(({ name, extension, templateSourceId, templateIdentifier }) => {
    if (name) {
      const promise: any = dispatch(requestAddDocumentFromTemplate({
        boardId: boardContext.id,
        documentSourceId,
        url: `${!url?.endsWith('/') ? `${url}/` : url}${name.trim()}.${extension}`,
        templateSourceId,
        templateIdentifier,
      }))

      promise.then(() => {
        if (urlRef.current === url) {
          refreshDocuments()
        }
      })

      handleAddItemClose()
    }
  }, [boardContext.id, documentSourceId, url, handleAddItemClose])

  const element = useMemo(() => DOCUMENTS.find(element => element.id === addingDocument), [addingDocument])

  return (
    <>
      <ToggleModal
        backdrop
        show={showToggleModal}
        onToggle={show => {
          if (!show) {
            setAddingDocument(false)
          }
        }}
        attachment='top center'
        targetAttachment='top center'
      >
        <ContentButton
          ref={buttonRef}
          onClick={handleAddItemClick}
        >
          <ContentIcon>
            <KanBoIcon name='kanbo-16-plus' />
          </ContentIcon>
          <ContentTitle>
            <FormattedMessage id='DOCUMENT_LIST_ADD_DOCUMENT' />
          </ContentTitle>
        </ContentButton>
        <div
          ref={modalRef}
        >
          {addingDocument === true ? (
            <Content>
              {DOCUMENTS.map(element =>
                <AddElementButton
                  key={element.id}
                  icon={<KanBoIcon name={element.iconName} />}
                  onClick={() => {
                    setAddingDocument(element.id)
                  }}
                >
                  <FormattedMessage id={element.formattedMessageId} />
                </AddElementButton>
              )}
            </Content>
          ) : (
            <AddOfficeDocumentContent>
              {element &&
                <ContentHeader>
                  <ElementContentIcon>
                    <KanBoIcon name={element.iconName} />
                  </ElementContentIcon>
                  <ContentTitle>
                    <FormattedMessage id={element.formattedMessageId} />
                  </ContentTitle>
                </ContentHeader>
              }
              <ContentBody>
                {addingDocument === DOCUMENT.WORD &&
                  <AddOfficeDocumentForm
                    initialValues={{
                      name: '',
                      extension: 'docx',
                      templateIdentifier: 'word-docx',
                    }}
                    onSubmit={handleAddDocumentFromTemplateSubmit}
                    onCancelClick={handleAddItemClose}
                  />
                }
                {addingDocument === DOCUMENT.EXCEL &&
                  <AddOfficeDocumentForm
                    initialValues={{
                      name: '',
                      extension: 'xlsx',
                      templateIdentifier: 'xlsx',
                    }}
                    onSubmit={handleAddDocumentFromTemplateSubmit}
                    onCancelClick={handleAddItemClose}
                  />
                }
                {addingDocument === DOCUMENT.POWERPOINT &&
                  <AddOfficeDocumentForm
                    initialValues={{
                      name: '',
                      extension: 'pptx',
                      templateIdentifier: 'pptx',
                    }}
                    onSubmit={handleAddDocumentFromTemplateSubmit}
                    onCancelClick={handleAddItemClose}
                  />
                }
              </ContentBody>
            </AddOfficeDocumentContent>
          )}
        </div>
      </ToggleModal>
      {addingDocument === DOCUMENT.FROM_TEMPLATE &&
        <ModalWithBoardContext
          boardId={boardContext.id}
          onClose={handleAddItemClose}
        >
          <BoardModalView
            title={<FormattedMessage id='DOCUMENT_ADD_FROM_TEMPLATE' />}
          >
            <DocumentTemplatesList
              elementId={documentFolderId}
              documentTemplateComponent={(props) =>
                <DocumentTemplateItem
                  {...props}
                  onClick={props.type === 'template' ? () => {
                    leftSidebar.push(() =>
                      <LeftSidebarView
                        title={<FormattedMessage id='DOCUMENT_ADD_FROM_TEMPLATE' />}
                      >
                        <FormGroup>
                          <ViewBody>
                            <ControlLabel><FormattedMessage id='DOCUMENT_ADD_FROM_TEMPLATE_SELECTED_DOCUMENT' /></ControlLabel>
                          </ViewBody>
                          <DocumentTemplateItem
                            {...props}
                          />
                        </FormGroup>
                        <ViewBody>
                          <AddOfficeDocumentForm
                            initialValues={{
                              name: '',
                              extension: props.extension,
                              templateSourceId: props.documentTemplateSourceId,
                              templateIdentifier: props.identifier
                            }}
                            onSubmit={handleAddDocumentFromTemplateSubmit}
                            onCancelClick={handleAddItemClose}
                          />
                        </ViewBody>
                      </LeftSidebarView>
                    )
                  } : props.onClick}
                />
              }
            />
          </BoardModalView>
        </ModalWithBoardContext>
      }
    </>
  )
}

export default withLeftSidebar(AddDocumentButton)